/**
 * @file components/sidebar/RecentChats.jsx
 * @description Displays the list of past conversations with management capabilities
 * REFACTORED: Now uses split contexts (useConversations) and CSS modules
 * 
 * @component RecentChats
 */

import React, { useState, useRef, useEffect } from 'react';

// --- Import specific context hooks ---
import { useConversations } from '../../context/ConversationsContext';

import ConversationActions from './ConversationActions';
import styles from '../../styles/Sidebar.module.css';
import actionStyles from '../../styles/ConversationActions.module.css';
import { MessageSquare, Clock } from 'lucide-react';

/** Maximum conversations shown in sidebar before "Show all" appears */
const MAX_SIDEBAR_CONVOS = 30;

/**
 * Formats the last updated time for display
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted time string
 */
const formatTime = (isoString) => {
    if (!isoString) return '';
    
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
    });
};

/**
 * Groups conversations by date categories
 * @param {Array} conversations - Array of conversation objects
 * @returns {Object} Grouped conversations
 */
const groupConversationsByDate = (conversations) => {
    const groups = {
        today: [],
        yesterday: [],
        thisWeek: [],
        older: []
    };
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    conversations.forEach(conv => {
        const convDate = new Date(conv.lastUpdated || conv.created_at);
        
        if (convDate >= today) {
            groups.today.push(conv);
        } else if (convDate >= yesterday) {
            groups.yesterday.push(conv);
        } else if (convDate >= weekAgo) {
            groups.thisWeek.push(conv);
        } else {
            groups.older.push(conv);
        }
    });
    
    return groups;
};

/**
 * Renders the list of recent conversations in the sidebar.
 * Uses useConversations for conversation management
 *
 * @param {object} props
 * @param {function} props.onLoadConversation - Callback function to execute when a chat is clicked.
 * @param {string} props.userId - The authenticated user's ID
 * @param {function} props.onShowAll - Opens the search/all-conversations view
 */
const RecentChats = ({ onLoadConversation, userId, onShowAll }) => {
    // --- Get conversation management from ConversationsContext ---
    const { 
        conversations, 
        handleRenameConversation 
    } = useConversations();
    
    // State for hover and inline editing management
    const [hoveredConversationId, setHoveredConversationId] = useState(null);
    const [editingConversationId, setEditingConversationId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const inputRef = useRef(null);

    // Focus the input when editing starts
    useEffect(() => {
        if (editingConversationId && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingConversationId]);

    // Filter conversations to only show those belonging to the current user
    // This is a safety check - the context should already only have user's conversations
    const userConversations = conversations.filter(conv => {
        // Check if conversation belongs to current user
        if (!userId) return true; // If no userId (shouldn't happen), show all
        if (!conv.userId) return true; // Legacy conversations without userId
        return conv.userId === userId;
    });
    
    console.log(`RecentChats: Displaying ${userConversations.length} conversations for user ${userId}`);

    const totalCount = userConversations.length;
    const hasMore = totalCount > MAX_SIDEBAR_CONVOS;

    // Slice to the cap before grouping so date headers only appear for visible items
    const visibleConversations = hasMore
        ? userConversations.slice(0, MAX_SIDEBAR_CONVOS)
        : userConversations;

    // Group conversations by date
    const groupedConversations = groupConversationsByDate(visibleConversations);

    // Check if there are any conversations
    const hasConversations = userConversations.length > 0;
    
    // Handler for successful conversation actions
    const handleStartRename = (convoId, currentTitle) => {
        setEditingConversationId(convoId);
        setEditingTitle(currentTitle || 'Untitled Chat');
        setHoveredConversationId(null); // Hide actions menu while editing
    };

    const handleCancelRename = () => {
        setEditingConversationId(null);
        setEditingTitle('');
    };

    const handleSaveRename = async () => {
        const convoId = editingConversationId;
        const originalConvo = conversations.find(c => c.id === convoId);
        if (!convoId || !editingTitle.trim() || editingTitle.trim() === originalConvo?.title) {
            handleCancelRename();
            return;
        }
        try {
            console.log(`RecentChats: Renaming conversation ${convoId} to "${editingTitle.trim()}"`);
            await handleRenameConversation(convoId, editingTitle.trim());
        } catch (error) {
            console.error("RecentChats: Failed to rename conversation:", error);
            // Context handles reverting on failure
        } finally {
            handleCancelRename();
        }
    };
    
    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') handleSaveRename();
        else if (e.key === 'Escape') handleCancelRename();
    };

    const renderConversationEntry = (convo) => {
        const isEditing = editingConversationId === convo.id;
        
        return (
            <div 
                key={convo.id} 
                className={styles.recentEntry} 
                onClick={!isEditing ? () => onLoadConversation(convo.id) : undefined}
                onMouseEnter={() => !isEditing && setHoveredConversationId(convo.id)}
                onMouseLeave={() => setHoveredConversationId(null)}
                role="button"
                tabIndex="0"
                onKeyDown={(e) => !isEditing && (e.key === 'Enter' || e.key === ' ') && onLoadConversation(convo.id)}
                title={convo.title}
                style={{ position: 'relative' }}
            >
                <MessageSquare size={18} />
                <div className={styles.recentEntryContent}>
                    {isEditing ? (
                        <input
                            ref={inputRef}
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={handleRenameKeyDown}
                            onBlur={handleSaveRename}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Enter a new title"
                            className={actionStyles.renameInput}
                        />
                    ) : (
                        <p className={styles.recentEntryP}>
                            {convo.title?.substring(0, 30) + (convo.title?.length > 30 ? '...' : '') || 'Untitled Chat'}
                        </p>
                    )}
                    <span className={styles.recentEntryTime}>
                        <Clock size={12} />
                        {formatTime(convo.lastUpdated)}
                    </span>
                </div>
                
                {hoveredConversationId === convo.id && !isEditing && (
                    <div 
                        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ConversationActions
                            conversationId={convo.id}
                            currentTitle={convo.title || 'Untitled Chat'}
                            onStartRename={() => handleStartRename(convo.id, convo.title)}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.recent}>
            <p className={styles.recentTitle}>Recent</p>
            {hasConversations ? (
                <>
                    {groupedConversations.today.length > 0 && (
                        <><div className={styles.recentGroup}><span className={styles.recentGroupTitle}>Today</span></div>
                        {groupedConversations.today.map(renderConversationEntry)}</>
                    )}
                    {groupedConversations.yesterday.length > 0 && (
                        <><div className={styles.recentGroup}><span className={styles.recentGroupTitle}>Yesterday</span></div>
                        {groupedConversations.yesterday.map(renderConversationEntry)}</>
                    )}
                    {groupedConversations.thisWeek.length > 0 && (
                        <><div className={styles.recentGroup}><span className={styles.recentGroupTitle}>This Week</span></div>
                        {groupedConversations.thisWeek.map(renderConversationEntry)}</>
                    )}
                    {groupedConversations.older.length > 0 && (
                        <><div className={styles.recentGroup}><span className={styles.recentGroupTitle}>Older</span></div>
                        {groupedConversations.older.map(renderConversationEntry)}</>
                    )}
                    {hasMore && (
                        <button
                            className={styles.showAllBtn}
                            onClick={onShowAll}
                        >
                            Show all {totalCount} chats
                        </button>
                    )}
                </>
            ) : (
                <div className={styles.noRecentChats}>
                    <MessageSquare size={32} className={styles.noChatsIcon} />
                    <p>No conversations yet</p>
                    <span>Start a new chat to begin</span>
                </div>
            )}
        </div>
    );
};

export default React.memo(RecentChats);